/******************************************************************************
 * @Author                : Hamza Damene                                      *
 * @CreatedDate           : 2025-03-19 15:00:00                               *
 * @LastEditors           : Hamza Damene                                      *
 * @LastEditDate          : 2025-03-19 15:00:00                               *
 *****************************************************************************/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { Link } from "react-router-dom";
import CustomerDisplay from "../../../../../components/data/customer/CustomerDisplay";
import TechnicianDisplay from "../../../../../components/data/technician/TechnicianDisplay";
import AddressDisplay from "../../../../../components/data/address/AddressDisplay";
import ContactDisplay from "../../../../../components/data/contact/ContactDisplay";
import PlannerDisplay from "../../../../../components/data/planner/PlannerDisplay";
import { t } from "i18next";

/**
 * Props pour le composant GeneralSection
 */
interface GeneralSectionProps {
  workorder: EasyTech.Workorder;
}

/**
 * CustomerField: the intervention's customer, linked to its profile.
 *
 * A real <Link> (so an <a href>) rather than an onClick + navigate(), so middle-click and cmd-click
 * open the profile in a new tab -- the intervention detail is a long form a reader may not want to
 * leave. /customers/:id sits behind ConnectedGuard with no RoleGuard, so anyone who can open this
 * intervention can open the customer too; no new authorisation surface.
 *
 * CustomerDisplay itself is deliberately left untouched: it is used in ~10 places, several of them
 * already inside a clickable card or list row, where nesting an anchor would change behaviour.
 */
function CustomerField({ customer }: { customer?: EasyTech.Customer }) {
  if (!customer) return <Typography>Aucun client</Typography>;

  // workorder.customer is a CustomerSimpleResponse; without an id the link would point at
  // /customers/undefined, so fall back to plain text rather than to a broken route.
  if (!customer.id) return <CustomerDisplay customer={customer} />;

  return (
    <Link
      to={`/customers/${customer.id}`}
      title={t("client:action:viewProfile")}
      // inline-block so w-fit applies: an inline <a> would let CustomerDisplay's w-full inner div
      // stretch the click and hover target across the whole grid cell.
      className="inline-block w-fit"
    >
      {/*
        * text-blue-600 hover:underline is the app's established clickable-text style -- the same
        * pair WorkorderDashCard, ProspectDashCard, QuotationDashCard, HoseDashCard and SearchBar
        * put on their Typography. The colour is what makes the name read as a link before the user
        * hovers it; the underline confirms it on hover.
        *
        * noToolTip because CustomerDisplay's tooltip just repeats the name, which is already fully
        * visible in this field (it wraps rather than truncating, unlike in the list rows that
        * tooltip exists for). Stacking it under the anchor's title would show two hover popups, so
        * the one that says something new -- what clicking does -- wins.
        */}
      <CustomerDisplay
        customer={customer}
        className="text-blue-600 hover:underline"
        noToolTip
      />
    </Link>
  );
}

/**
 * Section des informations générales d'un workorder
 * @param workorder - Données du workorder
 * @param informationHook - Hook contenant la logique d'édition
 * @returns Composant React pour la section générale
 */
export function GeneralSection({
  workorder,
}: GeneralSectionProps) {
  return (
    <>
      <div className="field">
        <label>{t("common:customer")}</label>
        <CustomerField customer={workorder?.customer} />
      </div>

      <div className="field">
        <label>{t("common:contact")}</label>
        {workorder?.contacts && workorder?.contacts.length > 0 ? (
            <ContactDisplay contact={workorder.contacts[0].contact} />
          ) : (
            <Typography>Aucun contact</Typography>
          )
        }
      </div>

      <div className="field">
        <label>{t("common:address")}</label>
          {workorder?.address ? (
            <AddressDisplay address={workorder.address} zone={workorder.zone} />
          ) : (
            <Typography>Aucune adresse</Typography>
          )}
      </div>
      
      <div className="field">
        <label>{t("workorder:informations:createdBy")}</label>
        {workorder?.planner ? (
          <PlannerDisplay planner={workorder.planner} />
        ) : (
          <Typography>Aucun planner</Typography>
        )}
      </div>

      <div className="field">
        <label>{t("common:technician")}</label>
          { workorder?.technician ? (
            <TechnicianDisplay technician={workorder.technician} />
          ) : (
            <Typography>Aucun technicien</Typography>
          )}
      </div>

      <div className="field">
        <label>{t("common:contactOnSite")}</label>
        {workorder?.onSiteContact ? (
            workorder?.onSiteContact.name+" "+workorder?.onSiteContact.phone
          ) : (
            <Typography>Aucun contact sur place</Typography>
          )
        }
      </div>

      {workorder?.purchaseOrderNumber && (
        <div className="field">
          <label>{t("workorder:infos:purchaseOrderNumber")}</label>
          <Typography>{workorder.purchaseOrderNumber}</Typography>
        </div>
      )}
    </>
  );
}
